/* Native Shopify facets with progressive section rendering. Links and GET forms
   remain usable without JavaScript; product filtering always happens on Shopify. */

/* Doit rester synchronisé avec le palier `lg` (1024px) de
   assets/premiom-catalogue.css : c'est là que le tiroir de filtres redevient
   une colonne permanente. 1023.98 plutôt que 1023 pour couvrir les largeurs
   fractionnaires des écrans à densité non entière. */
const PREMIOM_CATALOGUE_MOBILE_QUERY = '(max-width: 1023.98px)';

if (!customElements.get('premiom-catalogue')) {
  customElements.define('premiom-catalogue', class extends HTMLElement {
    connectedCallback() {
      this.lifecycle = new AbortController();
      const options = { signal: this.lifecycle.signal };
      this.mobile = matchMedia(PREMIOM_CATALOGUE_MOBILE_QUERY);
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('change', event => this.onChange(event), options);
      this.addEventListener('input', event => this.onInput(event), options);
      this.addEventListener('submit', event => {
        if (!event.target.matches('[data-filter-form]')) return;
        event.preventDefault();
        this.submitFilters();
      }, options);
      this.mobile.addEventListener('change', () => {
        this.querySelector('.catalogue-filters').open = !this.mobile.matches;
      }, options);
      window.addEventListener('popstate', () => this.load(location.href, { history: false }), options);
      this.querySelector('.catalogue-filters').open = !this.mobile.matches;
      this.classList.add('is-enhanced');

    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.request?.abort();
    }

    onClick(event) {
      if (event.target.closest('[data-show-results]')) {
        this.querySelector('.catalogue-filters').open = false;
        this.focusResults(true);
        return;
      }
      const link = event.target.closest('a[data-catalogue-link]');
      if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href);
      if (url.origin !== location.origin) return;
      event.preventDefault();
      if (!link.hasAttribute('data-page')) url.searchParams.delete('page');
      this.load(url, { toResults: link.hasAttribute('data-page'), focusResults: link.closest('.catalogue-active') !== null });
    }

    onChange(event) {
      if (event.target.matches('[data-sort], [data-filter-form] input:not([data-facet-search])')) {
        if (event.target.matches('[data-price-range]')) this.syncRange(event.target);
        this.submitFilters();
      }
    }

    onInput(event) {
      const input = event.target;
      if (input.matches('[data-facet-search]')) {
        const normalize = text => text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('fr');
        const group = input.closest('fieldset');
        const query = normalize(input.value.trim());
        const labels = [...group.querySelectorAll('.catalogue-filter-values .catalogue-filter-value')];
        labels.forEach(label => { label.hidden = !normalize(label.querySelector('span').textContent).includes(query); });
        group.querySelector('.catalogue-facet-empty').hidden = labels.some(label => !label.hidden);
      }
      if (input.matches('[data-price-range]')) this.syncRange(input);
      if (input.matches('[data-price-min], [data-price-max]')) {
        const group = input.closest('fieldset');
        const min = group.querySelector('[data-price-min]');
        const max = group.querySelector('[data-price-max]');
        const range = group.querySelector('[data-price-range]');
        max.min = min.value || '0';
        min.max = max.value || range.max;
        range.min = min.value || '0';
      }
    }

    syncRange(range) {
      const group = range.closest('fieldset');
      group.querySelector('[data-price-max]').value = range.value === range.max ? '' : range.value;
      group.querySelector('[data-price-min]').max = range.value;
      group.querySelector('output').value = new Intl.NumberFormat(document.documentElement.lang || 'fr', { style: 'currency', currency: range.dataset.currency || 'EUR' }).format(Number(range.value));
    }

    submitFilters() {
      const form = this.querySelector('[data-filter-form]');
      if (!form.reportValidity()) return;
      const url = new URL(form.action);
      const params = new URLSearchParams(location.search);
      // Keep unrelated Shopify preview/locale parameters, replace every facet.
      [...params.keys()].forEach(key => {
        if (key.startsWith('filter.') || ['page', 'sort_by', 'section_id'].includes(key)) params.delete(key);
      });
      for (const [key, value] of new FormData(form)) if (String(value).trim()) params.append(key, value);
      url.search = params.toString();
      this.load(url);
    }

    focusResults(scroll) {
      const results = this.querySelector('.catalogue-results');
      results.focus({ preventScroll: true });
      if (scroll) results.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    async load(destination, options = {}) {
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const url = new URL(destination, location.origin);
      url.searchParams.delete('section_id');
      const fetchUrl = new URL(url);
      fetchUrl.searchParams.set('section_id', this.dataset.sectionId);
      const open = this.querySelector('.catalogue-filters').open;
      const focusId = this.contains(document.activeElement) ? document.activeElement.id : '';
      const facets = [...this.querySelectorAll('fieldset')].map(field => ({
        key: field.dataset.facet,
        search: field.querySelector('[data-facet-search]')?.value,
        scroll: field.querySelector('.catalogue-filter-values')?.scrollTop || 0,
        open: field.querySelector('.catalogue-facet-disclosure')?.open
      }));
      this.classList.add('is-loading');
      this.querySelector('.catalogue-results').setAttribute('aria-busy', 'true');
      this.querySelector('[data-status]').textContent = 'Actualisation de la sélection…';
      this.querySelector('.catalogue-error').hidden = true;
      const timeout = setTimeout(() => request.abort('timeout'), 15000);
      try {
        const response = await fetch(fetchUrl, { signal: request.signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Section request failed');
        const document = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = document.querySelector('premiom-catalogue');
        if (!next?.querySelector('.catalogue-results')) throw new Error('Missing catalogue section');
        if (request !== this.request || request.signal.aborted) return;
        for (const selector of ['.catalogue-body', '.catalogue-universe-bar', '.catalogue-hero']) {
          this.querySelector(selector).replaceWith(next.querySelector(selector));
        }
        this.querySelector('.catalogue-filters').open = open;
        this.dataset.collectionUrl = next.dataset.collectionUrl;
        for (const facet of facets) {
          const group = [...this.querySelectorAll('fieldset')].find(field => field.dataset.facet === facet.key);
          const disclosure = group?.querySelector('.catalogue-facet-disclosure');
          if (disclosure && facet.open !== undefined) disclosure.open = facet.open;
          const search = group?.querySelector('[data-facet-search]');
          if (search && facet.search) { search.value = facet.search; this.onInput({ target: search }); }
          const values = group?.querySelector('.catalogue-filter-values');
          if (values) values.scrollTop = facet.scroll;
        }
        if (options.history !== false && url.href !== location.href) history.pushState({}, '', url);
        const focus = focusId && this.querySelector(`#${CSS.escape(focusId)}`);
        if (focus && !focus.disabled && !options.toResults) focus.focus({ preventScroll: true });
        else if (options.focusResults || options.toResults) this.focusResults(options.toResults);
        this.querySelector('[data-status]').textContent = this.querySelector('[data-result-count]').textContent;
        // The theme's product cards are Alpine components; Alpine mounts inserted
        // subtrees on the next observer tick. Wait for it so the event means the
        // price nodes and add-to-cart forms are actually in place.
        await new Promise(requestAnimationFrame);
        // Existing B2B integrations can observe the new product price nodes and
        // the add-to-cart forms that quick add renders on each card.
        this.dispatchEvent(new CustomEvent('premiom:catalogue:updated', { bubbles: true }));
      } catch (error) {
        if (request !== this.request || (request.signal.aborted && request.signal.reason !== 'timeout')) return;
        this.querySelector('[data-status]').textContent = '';
        this.querySelector('[data-retry]').href = url.href;
        this.querySelector('.catalogue-error').hidden = false;
      } finally {
        clearTimeout(timeout);
        if (request === this.request) {
          this.classList.remove('is-loading');
          this.querySelector('.catalogue-results').removeAttribute('aria-busy');
        }
      }
    }
  });
}
